import React, { useState } from 'react';
import { Menu, MenuItem, IconButton, Tooltip, Switch, Box, Button, Typography, Divider } from '@mui/material';
import WidgetsIcon from '@mui/icons-material/Widgets';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Props {
  actionButtonOrder: string[];
  setActionButtonOrder: (order: string[]) => void;
  actionButtonVisibility: Record<string, boolean>;
  setActionButtonVisibility: (visibility: Record<string, boolean>) => void;
  buttonsConfig: Record<string, { label: string; tooltipText: string; description: string }>;
  defaultOrder: string[];
  inactiveIds?: string[];
}

const SortableMenuItem = ({ id, label, isVisible, onVisibilityChange, isInactive, description }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
  };

  return (
    <MenuItem ref={setNodeRef} style={style} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title="Drag to reorder" arrow placement="top">
        <Box {...attributes} {...listeners} sx={{ display: 'flex', cursor: 'grab', color: 'text.secondary' }}>
          <DragIndicatorIcon fontSize="small" />
        </Box>
      </Tooltip>
      <Tooltip title="Toggle visibility" arrow placement="top">
        <Switch 
          size="small" 
          checked={isVisible} 
          onChange={(e) => onVisibilityChange(id, e.target.checked)} 
        />
      </Tooltip>
      <Tooltip title={description} arrow placement="top">
        <Typography variant="body2" sx={{ color: isInactive ? 'text.secondary' : 'text.primary', fontStyle: isInactive ? 'italic' : 'normal' }}>
          {label}
        </Typography>
      </Tooltip>
    </MenuItem>
  );
};

export const TableActionVisibilityMenu: React.FC<Props> = ({
  actionButtonOrder,
  setActionButtonOrder,
  actionButtonVisibility,
  setActionButtonVisibility,
  buttonsConfig,
  defaultOrder,
  inactiveIds = []
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleVisibilityChange = (id: string, isVisible: boolean) => {
    setActionButtonVisibility({
      ...actionButtonVisibility,
      [id]: isVisible,
    });
  };

  const handleHideAll = () => {
    const newVisibility: Record<string, boolean> = {};
    Object.keys(buttonsConfig).forEach(id => newVisibility[id] = false);
    setActionButtonVisibility(newVisibility);
  };

  const handleShowAll = () => {
    const newVisibility: Record<string, boolean> = {};
    Object.keys(buttonsConfig).forEach(id => newVisibility[id] = true);
    setActionButtonVisibility(newVisibility);
  };

  const handleResetOrder = () => {
    setActionButtonOrder(defaultOrder);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = actionButtonOrder.indexOf(active.id as string);
      const newIndex = actionButtonOrder.indexOf(over.id as string);
      setActionButtonOrder(arrayMove(actionButtonOrder, oldIndex, newIndex));
    }
  };

  const renderedOrder = actionButtonOrder.filter(id => buttonsConfig[id]);

  return (
    <>
      <Tooltip title="Show/Hide toolbar actions">
        <IconButton onClick={handleClick} size="small">
          <WidgetsIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { minWidth: 280, p: 0 } } }}
      >
        <Box sx={{ p: 1, display: 'flex', gap: 1, justifyContent: 'space-between' }}>
          <Button size="small" onClick={handleHideAll} disabled={Object.values(actionButtonVisibility).filter(v => v === false).length === Object.keys(buttonsConfig).length}>Hide All</Button>
          <Button size="small" onClick={handleResetOrder}>Reset Order</Button>
          <Button size="small" onClick={handleShowAll} disabled={Object.keys(buttonsConfig).every(id => actionButtonVisibility[id] !== false)}>Show All</Button>
        </Box>
        <Divider />
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={renderedOrder} strategy={verticalListSortingStrategy}>
            {renderedOrder.map(id => (
              <SortableMenuItem 
                key={id} 
                id={id} 
                label={buttonsConfig[id].label}
                description={buttonsConfig[id].description}
                isVisible={actionButtonVisibility[id] !== false}
                onVisibilityChange={handleVisibilityChange}
                isInactive={inactiveIds.includes(id)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Menu>
    </>
  );
};
